import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { PlayerIdentityRecordsResponse } from '@dayon/contracts';
import { EmptyState, Icon, Panel, useErrorDialog } from '@dayon/design-system';
import { api } from '../../lib/api.ts';
import { identityCaseHref } from './case-link.ts';
import { IdentityPhotos } from './IdentityPhotos.tsx';
import { RecordFactsList } from './RecordFacts.tsx';
import { manilaDay, photosGoneWords, type IdentityRecordFacts } from './record-words.ts';

/**
 * HOW THIS PLAYER WAS VERIFIED — the identity record on the player's KYC tab.
 *
 * Staging, 15 Oct: the player page said "Verified" and nothing else, its KYC tab said "No
 * submission on file … nothing to review", and the record behind the verdict was readable only
 * by a database query. PAGCOR items 13 and 21 make it evidence the operator must be able to
 * produce. The current record first, then the ones it replaced; each with how it was decided
 * and its photographs, opened through the one signed link Identity checks uses.
 *
 * Behind `kyc.review`, the key every identity route carries. No narrower read key exists that
 * the matrix gives the same roles: `players.view` opens this page for an Auditor and a Finance
 * Lead as well, and neither holds the key to somebody's identity document.
 */

/** The read behind the panel, and what the KYC tab's own sentences are decided from. */
export interface IdentityRead {
  state: 'not-allowed' | 'loading' | 'failed' | 'loaded';
  records: IdentityRecordFacts[];
  /**
   * The identity status the ACCOUNT carries, as the route read it in the same statement as the
   * records: one moment's answer, so the tab can compare the two. Absent until loaded.
   */
  accountStatus?: PlayerIdentityRecordsResponse['kycStatus'];
}

/**
 * Ask for the player's records — or, for a role without `kyc.review`, do not ask at all.
 *
 * The refusal is the reader's own capability, which this console already knows. A request
 * certain to be refused would put a forbidden in the log for an Auditor doing their job, and
 * its failure would raise an error dialog over a tab they are entitled to read.
 */
export async function readIdentityRecords(playerId: string, canReview: boolean): Promise<IdentityRead> {
  if (!canReview) return { state: 'not-allowed', records: [] };
  /* The route's answer as the contract types it (`playerIdentityRecords`), not as a copy here. */
  const data = await api.get<PlayerIdentityRecordsResponse>(
    `/api/kyc/identity/players/${encodeURIComponent(playerId)}`,
  );
  return { state: 'loaded', records: data.records, accountStatus: data.kycStatus };
}

/** The read, made when the tab opens and again when the player or the capability changes. */
export function usePlayerIdentityRecords(playerId: string, canReview: boolean): IdentityRead {
  const { showUnexpected } = useErrorDialog();
  const [read, setRead] = useState<IdentityRead>({
    state: canReview ? 'loading' : 'not-allowed',
    records: [],
  });

  useEffect(() => {
    let live = true;
    setRead({ state: canReview ? 'loading' : 'not-allowed', records: [] });
    readIdentityRecords(playerId, canReview)
      .then((result) => {
        if (live) setRead(result);
      })
      .catch((error: unknown) => {
        if (!live) return;
        setRead({ state: 'failed', records: [] });
        /* An error goes in the dialog; the panel only says the record did not load. */
        showUnexpected(error, 'Could not read this player’s identity record');
      });
    return () => {
      live = false;
    };
  }, [playerId, canReview, showUnexpected]);

  return read;
}

/** The sentence for a role without the key: the key, the reader's role, and who to ask. */
export function identityRecordWithoutKey(role: string): string {
  return `Seeing how this player was verified needs kyc.review, which ${role} does not hold. Ask an administrator if you need it.`;
}

export function PlayerIdentityRecords({
  read,
  role,
  playerRef,
}: {
  read: IdentityRead;
  role: string;
  playerRef: string;
}) {
  const current = read.records.filter((r) => r.current);
  const replaced = read.records.filter((r) => !r.current);

  return (
    <Panel title="Identity record">
      {read.state === 'not-allowed' && (
        <div className="notice notice-quiet">
          <Icon name="lock-key" />
          <div>{identityRecordWithoutKey(role)}</div>
        </div>
      )}
      {read.state === 'loading' && <p className="text-subtle">Reading the identity record…</p>}
      {read.state === 'failed' && (
        <EmptyState icon="warning-circle" title="The identity record did not load">
          The message that opened says why. Open this tab again to try once more.
        </EmptyState>
      )}
      {read.state === 'loaded' && read.records.length === 0 && (
        <EmptyState icon="identification-card" title="No identity check on file">
          This player has not started an identity check: no ID and no live photo have been sent.
        </EmptyState>
      )}
      {read.state === 'loaded' && read.records.length > 0 && (
        <div className="stack" style={{ gap: 'var(--space-6)' }}>
          {current.map((r) => (
            <RecordBlock key={r.id} record={r} playerRef={playerRef} />
          ))}
          {replaced.length > 0 && (
            <div className="stack">
              <strong>Earlier checks, newest first</strong>
              <p className="text-subtle" style={{ margin: 0 }}>
                A check is replaced, not changed, when the player is given a new live-photo link, or
                sends new photos after a refusal — of their own, or once support allows one more
                try. Each replaced one stays on file as it was.
              </p>
              {replaced.map((r) => (
                <RecordBlock key={r.id} record={r} playerRef={playerRef} />
              ))}
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

/* One record: how it was decided, then its photographs. */
function RecordBlock({ record, playerRef }: { record: IdentityRecordFacts; playerRef: string }) {
  return (
    <div
      className="stack"
      style={{
        paddingTop: 'var(--space-4)',
        borderTop: '1px solid var(--color-divider)',
      }}
    >
      {/* Which check this is; its outcome is the first row below, said once. */}
      {record.current ? (
        <strong>The current check</strong>
      ) : (
        <span className="text-subtle">
          Replaced by a newer check on {manilaDay(record.supersededAt!)}
        </span>
      )}
      <RecordFactsList record={record} />
      {/* THE CURRENT REFUSAL LEADS TO ITS CASE, where support may allow one more try — or reads why
          not. Staff find a player here, from Player management; the Decided list cannot be searched
          by player. */}
      {record.current && record.outcome === 'REFUSED' && (
        <p className="text-subtle" style={{ margin: 0 }}>
          Whether support can allow one more try is decided on the check itself.{' '}
          <Link to={identityCaseHref(record.id)}>Open this check in Identity checks</Link>
        </p>
      )}
      <IdentityPhotos
        key={record.id}
        recordId={record.id}
        playerRef={playerRef}
        imagesHeld={record.imagesHeld}
        empty={photosGoneWords(record.imagesPurgedAt)}
      />
    </div>
  );
}
